/**
 * Employee View Page — Comprehensive profile with tabbed interface
 * Shows Overview, Education, Experience, Bank Details, Emergency Contacts,
 * Documents, Goals, and Leave tabs. Each sub-tab supports Add / Edit / Delete.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne, useList, useCreate, useUpdate, useDelete } from '@refinedev/core';
import {
  Button,
  Card,
  CardContent,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Badge,
  Avatar,
  AvatarImage,
  AvatarFallback,
  StatusBadge,
  EmptyState,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  Input,
  Label,
  Textarea,
  Checkbox,
} from '@yesbos/ui';
import {
  ArrowLeft,
  Pencil,
  Mail,
  Phone,
  Building2,
  Briefcase,
  BookOpen,
  CreditCard,
  FileText,
  Target,
  Calendar,
  Users,
  AlertCircle,
  Plus,
  Trash2,
  Edit2,
  MapPin,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EducationRecord {
  id: string;
  institution: string;
  degree: string;
  field_of_study: string;
  start_date: string;
  end_date?: string;
  grade?: string;
}

interface ExperienceRecord {
  id: string;
  company_name: string;
  job_title: string;
  start_date: string;
  end_date?: string;
  description?: string;
}

interface BankRecord {
  id: string;
  bank_name: string;
  account_number: string;
  ifsc_code: string;
  branch?: string;
  is_primary?: boolean;
}

interface EmergencyContactRecord {
  id: string;
  name: string;
  relationship: string;
  phone: string;
  email?: string;
}

interface DocumentRecord {
  id: string;
  document_type: string;
  title: string;
  file_url?: string;
  expiry_date?: string;
}

// ─── Inline Components ────────────────────────────────────────────────────────

const SectionError = ({ section }: { section: string }) => (
  <div className="flex items-start gap-3 rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5 p-4">
    <AlertCircle className="h-5 w-5 text-[var(--color-destructive)] mt-0.5 flex-shrink-0" />
    <div>
      <p className="text-sm font-medium text-[var(--color-destructive)]">Failed to load {section}</p>
      <p className="text-xs text-[var(--color-muted-foreground)] mt-1">
        Something went wrong while fetching data. Please try refreshing the page.
      </p>
    </div>
  </div>
);

const Spinner = ({ label }: { label: string }) => (
  <div className="flex items-center justify-center py-8">
    <div className="text-center space-y-2">
      <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-border)] border-t-[var(--color-primary)]" />
      <p className="text-sm text-[var(--color-muted-foreground)]">{label}</p>
    </div>
  </div>
);

const FieldRow = ({ label, value }: { label: string; value?: string | null }) => {
  if (!value) return null;
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{value}</dd>
    </div>
  );
};

// ─── Sub-Tab Header ───────────────────────────────────────────────────────────

const TabHeader = ({
  title,
  count,
  onAdd,
}: {
  title: string;
  count?: number;
  onAdd: () => void;
}) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <h3 className="text-sm font-semibold">{title}</h3>
      {count !== undefined && count > 0 && (
        <Badge variant="secondary">{count}</Badge>
      )}
    </div>
    <Button size="sm" onClick={onAdd} className="gap-1">
      <Plus className="h-4 w-4" />
      Add
    </Button>
  </div>
);

// ─── Record Card Row ──────────────────────────────────────────────────────────

const RecordCard = ({
  children,
  onEdit,
  onDelete,
}: {
  children: React.ReactNode;
  onEdit: () => void;
  onDelete: () => void;
}) => (
  <div className="flex items-start justify-between rounded-lg border border-[var(--color-border)] p-4 mb-3">
    <div className="flex-1">{children}</div>
    <div className="flex gap-1 ml-4 flex-shrink-0">
      <Button variant="ghost" size="icon" onClick={onEdit} title="Edit">
        <Edit2 className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={onDelete}
        title="Delete"
        className="text-[var(--color-destructive)] hover:text-[var(--color-destructive)]"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  </div>
);

// ─── Form Field Wrappers ──────────────────────────────────────────────────────

const FormField = ({
  id,
  label,
  required,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) => (
  <div className="space-y-1">
    <Label htmlFor={id}>
      {label}
      {required && <span className="text-[var(--color-destructive)] ml-1">*</span>}
    </Label>
    {children}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export function EmployeeViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState('overview');

  // ─── Modal state ──────────────────────────────────────────────────────

  type ModalType =
    | 'education'
    | 'experience'
    | 'bank'
    | 'emergency'
    | 'documents'
    | null;

  const [openModal, setOpenModal] = React.useState<ModalType>(null);
  const [editRecord, setEditRecord] = React.useState<Record<string, unknown> | null>(null);

  const openAdd = (type: ModalType) => {
    setEditRecord(null);
    setOpenModal(type);
  };
  const openEdit = (type: ModalType, record: Record<string, unknown>) => {
    setEditRecord(record);
    setOpenModal(type);
  };
  const closeModal = () => {
    setOpenModal(null);
    setEditRecord(null);
  };

  // ─── Data Fetching ────────────────────────────────────────────────────

  const { data: employeeData, isLoading: employeeLoading } = useOne({
    resource: 'employees',
    id: id!,
  });

  const {
    data: educationData,
    isLoading: educationLoading,
    isError: educationError,
    refetch: refetchEducation,
  } = useList({
    resource: 'employee_education',
    filters: [{ field: 'employee_id', operator: 'eq', value: id }],
    queryOptions: { enabled: !!id },
  });

  const {
    data: experienceData,
    isLoading: experienceLoading,
    isError: experienceError,
    refetch: refetchExperience,
  } = useList({
    resource: 'employee_experience',
    filters: [{ field: 'employee_id', operator: 'eq', value: id }],
    queryOptions: { enabled: !!id },
  });

  const {
    data: bankDetailsData,
    isLoading: bankDetailsLoading,
    isError: bankDetailsError,
    refetch: refetchBank,
  } = useList({
    resource: 'employee_bank_details',
    filters: [{ field: 'employee_id', operator: 'eq', value: id }],
    queryOptions: { enabled: !!id },
  });

  const {
    data: emergencyContactsData,
    isLoading: emergencyContactsLoading,
    isError: emergencyContactsError,
    refetch: refetchEmergency,
  } = useList({
    resource: 'employee_emergency_contacts',
    filters: [{ field: 'employee_id', operator: 'eq', value: id }],
    queryOptions: { enabled: !!id },
  });

  const {
    data: documentsData,
    isLoading: documentsLoading,
    isError: documentsError,
    refetch: refetchDocuments,
  } = useList({
    resource: 'employee_documents',
    filters: [{ field: 'employee_id', operator: 'eq', value: id }],
    queryOptions: { enabled: !!id },
  });

  const {
    data: goalsData,
    isLoading: goalsLoading,
    isError: goalsError,
  } = useList({
    resource: 'goals',
    filters: [{ field: 'employee_id', operator: 'eq', value: id }],
    queryOptions: { enabled: !!id },
  });

  const {
    data: leaveData,
    isLoading: leaveLoading,
    isError: leaveError,
  } = useList({
    resource: 'leave_requests',
    filters: [{ field: 'employee_id', operator: 'eq', value: id }],
    queryOptions: { enabled: !!id },
  });

  // ─── Mutations ────────────────────────────────────────────────────────

  const { mutate: createRecord } = useCreate();
  const { mutate: updateRecord } = useUpdate();
  const { mutate: deleteRecord } = useDelete();

  const handleDelete = (resource: string, recordId: string, refetch: () => void) => {
    if (!confirm('Delete this record? This cannot be undone.')) return;
    deleteRecord(
      { resource, id: recordId },
      { onSuccess: () => refetch() },
    );
  };

  // ─── Employee data ────────────────────────────────────────────────────

  const employee = employeeData?.data ?? {};

  const getInitials = (name?: string) => {
    if (!name) return 'E';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (employeeLoading) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/employees')} disabled>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Directory
        </Button>
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Spinner label="Loading employee profile..." />
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/employees')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={String(employee.avatar_url ?? '')} />
              <AvatarFallback className="bg-[var(--color-primary)] text-[var(--color-primary-foreground)] text-lg font-semibold">
                {getInitials(String(employee.full_name ?? ''))}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold">
                {String(employee.full_name ?? `${employee.first_name ?? ''} ${employee.last_name ?? ''}`.trim())}
              </h1>
              <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
                <Briefcase className="h-4 w-4" />
                <span>{String(employee.designation_name ?? 'Not Set')}</span>
              </div>
              {employee.employment_status && (
                <StatusBadge status={String(employee.employment_status)} className="mt-2" />
              )}
            </div>
          </div>
        </div>
        <Button onClick={() => navigate(`/employees/${id}/edit`)} className="gap-2">
          <Pencil className="h-4 w-4" />
          Edit Profile
        </Button>
      </div>

      {/* Quick Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6 space-y-2">
            <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
              <Mail className="h-4 w-4" />
              <span>Email</span>
            </div>
            <p className="font-medium text-sm">{String(employee.email ?? 'Not provided')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 space-y-2">
            <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
              <Phone className="h-4 w-4" />
              <span>Phone</span>
            </div>
            <p className="font-medium text-sm">{String(employee.phone ?? 'Not provided')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 space-y-2">
            <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
              <Building2 className="h-4 w-4" />
              <span>Department</span>
            </div>
            <p className="font-medium text-sm">{String(employee.department_name ?? 'Not Set')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full justify-start border-b border-[var(--color-border)] rounded-none bg-transparent p-0 overflow-x-auto flex-wrap">
            {[
              { value: 'overview', icon: <BookOpen className="mr-2 h-4 w-4" />, label: 'Overview' },
              { value: 'education', icon: <BookOpen className="mr-2 h-4 w-4" />, label: 'Education' },
              { value: 'experience', icon: <Briefcase className="mr-2 h-4 w-4" />, label: 'Experience' },
              { value: 'bank', icon: <CreditCard className="mr-2 h-4 w-4" />, label: 'Bank Details' },
              { value: 'emergency', icon: <Users className="mr-2 h-4 w-4" />, label: 'Emergency Contacts' },
              { value: 'documents', icon: <FileText className="mr-2 h-4 w-4" />, label: 'Documents' },
              { value: 'goals', icon: <Target className="mr-2 h-4 w-4" />, label: 'Goals' },
              { value: 'leave', icon: <Calendar className="mr-2 h-4 w-4" />, label: 'Leave' },
            ].map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[var(--color-primary)] data-[state=active]:bg-transparent whitespace-nowrap"
              >
                {tab.icon}
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* ── Overview ── */}
          <TabsContent value="overview" className="p-6 space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Personal Information</h3>
              <dl className="grid grid-cols-2 gap-x-8 gap-y-4">
                <FieldRow label="Employee Code" value={String(employee.employee_code ?? '')} />
                <FieldRow label="Date of Birth" value={String(employee.date_of_birth ?? '').slice(0, 10)} />
                <FieldRow label="Gender" value={String(employee.gender ?? '')} />
                <FieldRow label="Blood Group" value={String(employee.blood_group ?? '')} />
                <FieldRow label="Marital Status" value={String(employee.marital_status ?? '')} />
              </dl>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Employment Details</h3>
              <dl className="grid grid-cols-2 gap-x-8 gap-y-4">
                <FieldRow label="Date of Joining" value={String(employee.date_of_joining ?? '').slice(0, 10)} />
                <FieldRow label="Confirmation Date" value={String(employee.date_of_confirmation ?? '').slice(0, 10)} />
                <FieldRow label="Employment Type" value={String(employee.employment_type ?? '').replace(/_/g, ' ')} />
                <FieldRow label="Status" value={String(employee.employment_status ?? '')} />
              </dl>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Contact & Address</h3>
              <dl className="grid grid-cols-2 gap-x-8 gap-y-4">
                <FieldRow label="Email" value={String(employee.email ?? '')} />
                <FieldRow label="Phone" value={String(employee.phone ?? '')} />
                <div className="col-span-2">
                  <FieldRow label="Current Address" value={String(employee.current_address ?? '')} />
                </div>
                <FieldRow label="City" value={String(employee.city ?? '')} />
                <FieldRow label="State" value={String(employee.state ?? '')} />
                <FieldRow label="Pincode" value={String(employee.pincode ?? '')} />
              </dl>
            </div>
            {(employee.pan || employee.aadhaar || employee.uan || employee.pf_number) && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Government IDs</h3>
                <dl className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <FieldRow label="PAN" value={String(employee.pan ?? '')} />
                  <FieldRow label="Aadhaar" value={String(employee.aadhaar ?? '')} />
                  <FieldRow label="UAN" value={String(employee.uan ?? '')} />
                  <FieldRow label="PF Number" value={String(employee.pf_number ?? '')} />
                  <FieldRow label="ESI Number" value={String(employee.esi_number ?? '')} />
                </dl>
              </div>
            )}
          </TabsContent>

          {/* ── Education ── */}
          <TabsContent value="education" className="p-6">
            <TabHeader
              title="Education History"
              count={educationData?.data?.length}
              onAdd={() => openAdd('education')}
            />
            {educationError ? (
              <SectionError section="education records" />
            ) : educationLoading ? (
              <Spinner label="Loading education records..." />
            ) : educationData?.data && educationData.data.length > 0 ? (
              educationData.data.map((rec) => {
                const r = rec as unknown as EducationRecord;
                return (
                  <RecordCard
                    key={r.id}
                    onEdit={() => openEdit('education', rec as Record<string, unknown>)}
                    onDelete={() => handleDelete('employee_education', r.id, refetchEducation)}
                  >
                    <p className="font-medium text-sm">{r.institution}</p>
                    <p className="text-sm text-muted-foreground">
                      {r.degree}{r.field_of_study ? ` — ${r.field_of_study}` : ''}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {r.start_date?.slice(0, 10)} – {r.end_date?.slice(0, 10) ?? 'Present'}
                      {r.grade ? ` · Grade: ${r.grade}` : ''}
                    </p>
                  </RecordCard>
                );
              })
            ) : (
              <EmptyState
                title="No Education Records"
                description="No education history has been added yet."
                icon={<BookOpen className="h-12 w-12" />}
              />
            )}
          </TabsContent>

          {/* ── Experience ── */}
          <TabsContent value="experience" className="p-6">
            <TabHeader
              title="Work Experience"
              count={experienceData?.data?.length}
              onAdd={() => openAdd('experience')}
            />
            {experienceError ? (
              <SectionError section="work experience" />
            ) : experienceLoading ? (
              <Spinner label="Loading experience records..." />
            ) : experienceData?.data && experienceData.data.length > 0 ? (
              experienceData.data.map((rec) => {
                const r = rec as unknown as ExperienceRecord;
                return (
                  <RecordCard
                    key={r.id}
                    onEdit={() => openEdit('experience', rec as Record<string, unknown>)}
                    onDelete={() => handleDelete('employee_experience', r.id, refetchExperience)}
                  >
                    <p className="font-medium text-sm">{r.job_title}</p>
                    <p className="text-sm text-muted-foreground">{r.company_name}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {r.start_date?.slice(0, 10)} – {r.end_date?.slice(0, 10) ?? 'Present'}
                    </p>
                    {r.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.description}</p>
                    )}
                  </RecordCard>
                );
              })
            ) : (
              <EmptyState
                title="No Work Experience"
                description="No work experience has been added yet."
                icon={<Briefcase className="h-12 w-12" />}
              />
            )}
          </TabsContent>

          {/* ── Bank Details ── */}
          <TabsContent value="bank" className="p-6">
            <TabHeader
              title="Bank Accounts"
              count={bankDetailsData?.data?.length}
              onAdd={() => openAdd('bank')}
            />
            {bankDetailsError ? (
              <SectionError section="bank details" />
            ) : bankDetailsLoading ? (
              <Spinner label="Loading bank details..." />
            ) : bankDetailsData?.data && bankDetailsData.data.length > 0 ? (
              bankDetailsData.data.map((rec) => {
                const r = rec as unknown as BankRecord;
                return (
                  <RecordCard
                    key={r.id}
                    onEdit={() => openEdit('bank', rec as Record<string, unknown>)}
                    onDelete={() => handleDelete('employee_bank_details', r.id, refetchBank)}
                  >
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{r.bank_name}</p>
                      {r.is_primary && <Badge variant="secondary" className="text-xs">Primary</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground font-mono">
                      Account: {r.account_number}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      IFSC: {r.ifsc_code}{r.branch ? ` · Branch: ${r.branch}` : ''}
                    </p>
                  </RecordCard>
                );
              })
            ) : (
              <EmptyState
                title="No Bank Details"
                description="No bank account information has been added yet."
                icon={<CreditCard className="h-12 w-12" />}
              />
            )}
          </TabsContent>

          {/* ── Emergency Contacts ── */}
          <TabsContent value="emergency" className="p-6">
            <TabHeader
              title="Emergency Contacts"
              count={emergencyContactsData?.data?.length}
              onAdd={() => openAdd('emergency')}
            />
            {emergencyContactsError ? (
              <SectionError section="emergency contacts" />
            ) : emergencyContactsLoading ? (
              <Spinner label="Loading emergency contacts..." />
            ) : emergencyContactsData?.data && emergencyContactsData.data.length > 0 ? (
              emergencyContactsData.data.map((rec) => {
                const r = rec as unknown as EmergencyContactRecord;
                return (
                  <RecordCard
                    key={r.id}
                    onEdit={() => openEdit('emergency', rec as Record<string, unknown>)}
                    onDelete={() => handleDelete('employee_emergency_contacts', r.id, refetchEmergency)}
                  >
                    <p className="font-medium text-sm">{r.name}</p>
                    <p className="text-sm text-muted-foreground">{r.relationship}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {r.phone}{r.email ? ` · ${r.email}` : ''}
                    </p>
                  </RecordCard>
                );
              })
            ) : (
              <EmptyState
                title="No Emergency Contacts"
                description="No emergency contact information has been added yet."
                icon={<Users className="h-12 w-12" />}
              />
            )}
          </TabsContent>

          {/* ── Documents ── */}
          <TabsContent value="documents" className="p-6">
            <TabHeader
              title="Documents"
              count={documentsData?.data?.length}
              onAdd={() => openAdd('documents')}
            />
            {documentsError ? (
              <SectionError section="documents" />
            ) : documentsLoading ? (
              <Spinner label="Loading documents..." />
            ) : documentsData?.data && documentsData.data.length > 0 ? (
              documentsData.data.map((rec) => {
                const r = rec as unknown as DocumentRecord;
                return (
                  <RecordCard
                    key={r.id}
                    onEdit={() => openEdit('documents', rec as Record<string, unknown>)}
                    onDelete={() => handleDelete('employee_documents', r.id, refetchDocuments)}
                  >
                    <p className="font-medium text-sm">{r.title}</p>
                    <p className="text-sm text-muted-foreground capitalize">{r.document_type?.replace(/_/g, ' ')}</p>
                    {r.expiry_date && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Expires: {r.expiry_date?.slice(0, 10)}
                      </p>
                    )}
                    {r.file_url && (
                      <a
                        href={r.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[var(--color-primary)] hover:underline mt-1 block"
                      >
                        View Document
                      </a>
                    )}
                  </RecordCard>
                );
              })
            ) : (
              <EmptyState
                title="No Documents"
                description="No documents have been uploaded yet."
                icon={<FileText className="h-12 w-12" />}
              />
            )}
          </TabsContent>

          {/* ── Goals ── */}
          <TabsContent value="goals" className="p-6">
            {goalsError ? (
              <SectionError section="goals" />
            ) : goalsLoading ? (
              <Spinner label="Loading goals..." />
            ) : goalsData?.data && goalsData.data.length > 0 ? (
              goalsData.data.map((rec) => (
                <div
                  key={String(rec.id)}
                  className="rounded-lg border border-[var(--color-border)] p-4 mb-3"
                >
                  <p className="font-medium text-sm">{String(rec.title ?? '')}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Status: {String(rec.status ?? '')} · Due: {String(rec.due_date ?? '').slice(0, 10)}
                  </p>
                </div>
              ))
            ) : (
              <EmptyState
                title="No Goals"
                description="No goals have been set yet."
                icon={<Target className="h-12 w-12" />}
              />
            )}
          </TabsContent>

          {/* ── Leave ── */}
          <TabsContent value="leave" className="p-6">
            {leaveError ? (
              <SectionError section="leave records" />
            ) : leaveLoading ? (
              <Spinner label="Loading leave records..." />
            ) : leaveData?.data && leaveData.data.length > 0 ? (
              leaveData.data.map((rec) => (
                <div
                  key={String(rec.id)}
                  className="rounded-lg border border-[var(--color-border)] p-4 mb-3"
                >
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm">{String(rec.leave_type_name ?? rec.leave_type_id ?? '')}</p>
                    <Badge variant={String(rec.status) === 'approved' ? 'default' : 'secondary'}>
                      {String(rec.status ?? '')}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {String(rec.start_date ?? '').slice(0, 10)} – {String(rec.end_date ?? '').slice(0, 10)}
                    {rec.reason ? ` · ${String(rec.reason)}` : ''}
                  </p>
                </div>
              ))
            ) : (
              <EmptyState
                title="No Leave Requests"
                description="No leave requests found."
                icon={<Calendar className="h-12 w-12" />}
              />
            )}
          </TabsContent>
        </Tabs>
      </Card>

      {/* ── Modals ── */}
      <EducationModal
        open={openModal === 'education'}
        record={editRecord}
        employeeId={id!}
        onClose={closeModal}
        onSaved={() => { refetchEducation(); closeModal(); }}
        createRecord={createRecord}
        updateRecord={updateRecord}
      />
      <ExperienceModal
        open={openModal === 'experience'}
        record={editRecord}
        employeeId={id!}
        onClose={closeModal}
        onSaved={() => { refetchExperience(); closeModal(); }}
        createRecord={createRecord}
        updateRecord={updateRecord}
      />
      <BankModal
        open={openModal === 'bank'}
        record={editRecord}
        employeeId={id!}
        onClose={closeModal}
        onSaved={() => { refetchBank(); closeModal(); }}
        createRecord={createRecord}
        updateRecord={updateRecord}
      />
      <EmergencyModal
        open={openModal === 'emergency'}
        record={editRecord}
        employeeId={id!}
        onClose={closeModal}
        onSaved={() => { refetchEmergency(); closeModal(); }}
        createRecord={createRecord}
        updateRecord={updateRecord}
      />
      <DocumentModal
        open={openModal === 'documents'}
        record={editRecord}
        employeeId={id!}
        onClose={closeModal}
        onSaved={() => { refetchDocuments(); closeModal(); }}
        createRecord={createRecord}
        updateRecord={updateRecord}
      />
    </div>
  );
}

// ─── Education Modal ──────────────────────────────────────────────────────────

function EducationModal({
  open,
  record,
  employeeId,
  onClose,
  onSaved,
  createRecord,
  updateRecord,
}: {
  open: boolean;
  record: Record<string, unknown> | null;
  employeeId: string;
  onClose: () => void;
  onSaved: () => void;
  createRecord: ReturnType<typeof useCreate>['mutate'];
  updateRecord: ReturnType<typeof useUpdate>['mutate'];
}) {
  const isEdit = !!record;
  const [form, setForm] = React.useState({
    institution: '',
    degree: '',
    field_of_study: '',
    start_date: '',
    end_date: '',
    grade: '',
  });

  React.useEffect(() => {
    if (record) {
      setForm({
        institution: String(record.institution ?? ''),
        degree: String(record.degree ?? ''),
        field_of_study: String(record.field_of_study ?? ''),
        start_date: String(record.start_date ?? '').slice(0, 10),
        end_date: String(record.end_date ?? '').slice(0, 10),
        grade: String(record.grade ?? ''),
      });
    } else {
      setForm({ institution: '', degree: '', field_of_study: '', start_date: '', end_date: '', grade: '' });
    }
  }, [record, open]);

  const f = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = () => {
    const values = { ...form, employee_id: employeeId };
    if (isEdit) {
      updateRecord(
        { resource: 'employee_education', id: String(record!.id), values },
        { onSuccess: onSaved },
      );
    } else {
      createRecord(
        { resource: 'employee_education', values },
        { onSuccess: onSaved },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Education' : 'Add Education'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <FormField id="institution" label="Institution" required>
            <Input id="institution" value={form.institution} onChange={f('institution')} placeholder="University / College name" />
          </FormField>
          <FormField id="degree" label="Degree" required>
            <Input id="degree" value={form.degree} onChange={f('degree')} placeholder="B.Tech, MBA, etc." />
          </FormField>
          <FormField id="field_of_study" label="Field of Study">
            <Input id="field_of_study" value={form.field_of_study} onChange={f('field_of_study')} placeholder="Computer Science, Finance, etc." />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField id="start_date" label="Start Date" required>
              <Input id="start_date" type="date" value={form.start_date} onChange={f('start_date')} />
            </FormField>
            <FormField id="end_date" label="End Date">
              <Input id="end_date" type="date" value={form.end_date} onChange={f('end_date')} />
            </FormField>
          </div>
          <FormField id="grade" label="Grade / GPA">
            <Input id="grade" value={form.grade} onChange={f('grade')} placeholder="A, 8.5, etc." />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!form.institution || !form.degree || !form.start_date}>
            {isEdit ? 'Update' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Experience Modal ─────────────────────────────────────────────────────────

function ExperienceModal({
  open,
  record,
  employeeId,
  onClose,
  onSaved,
  createRecord,
  updateRecord,
}: {
  open: boolean;
  record: Record<string, unknown> | null;
  employeeId: string;
  onClose: () => void;
  onSaved: () => void;
  createRecord: ReturnType<typeof useCreate>['mutate'];
  updateRecord: ReturnType<typeof useUpdate>['mutate'];
}) {
  const isEdit = !!record;
  const [form, setForm] = React.useState({
    company_name: '',
    job_title: '',
    start_date: '',
    end_date: '',
    description: '',
  });

  React.useEffect(() => {
    if (record) {
      setForm({
        company_name: String(record.company_name ?? ''),
        job_title: String(record.job_title ?? ''),
        start_date: String(record.start_date ?? '').slice(0, 10),
        end_date: String(record.end_date ?? '').slice(0, 10),
        description: String(record.description ?? ''),
      });
    } else {
      setForm({ company_name: '', job_title: '', start_date: '', end_date: '', description: '' });
    }
  }, [record, open]);

  const f = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = () => {
    const values = { ...form, employee_id: employeeId };
    if (isEdit) {
      updateRecord(
        { resource: 'employee_experience', id: String(record!.id), values },
        { onSuccess: onSaved },
      );
    } else {
      createRecord(
        { resource: 'employee_experience', values },
        { onSuccess: onSaved },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Experience' : 'Add Experience'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <FormField id="company_name" label="Company Name" required>
            <Input id="company_name" value={form.company_name} onChange={f('company_name')} placeholder="Company Ltd." />
          </FormField>
          <FormField id="job_title" label="Job Title" required>
            <Input id="job_title" value={form.job_title} onChange={f('job_title')} placeholder="Software Engineer, Manager, etc." />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField id="exp_start_date" label="Start Date" required>
              <Input id="exp_start_date" type="date" value={form.start_date} onChange={f('start_date')} />
            </FormField>
            <FormField id="exp_end_date" label="End Date">
              <Input id="exp_end_date" type="date" value={form.end_date} onChange={f('end_date')} />
            </FormField>
          </div>
          <FormField id="description" label="Description">
            <Textarea
              id="description"
              value={form.description}
              onChange={f('description')}
              placeholder="Brief description of responsibilities..."
              rows={3}
            />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!form.company_name || !form.job_title || !form.start_date}>
            {isEdit ? 'Update' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Bank Modal ───────────────────────────────────────────────────────────────

function BankModal({
  open,
  record,
  employeeId,
  onClose,
  onSaved,
  createRecord,
  updateRecord,
}: {
  open: boolean;
  record: Record<string, unknown> | null;
  employeeId: string;
  onClose: () => void;
  onSaved: () => void;
  createRecord: ReturnType<typeof useCreate>['mutate'];
  updateRecord: ReturnType<typeof useUpdate>['mutate'];
}) {
  const isEdit = !!record;
  const [form, setForm] = React.useState({
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    branch: '',
    is_primary: true,
  });

  React.useEffect(() => {
    if (record) {
      setForm({
        bank_name: String(record.bank_name ?? ''),
        account_number: String(record.account_number ?? ''),
        ifsc_code: String(record.ifsc_code ?? ''),
        branch: String(record.branch ?? ''),
        is_primary: Boolean(record.is_primary ?? false),
      });
    } else {
      setForm({ bank_name: '', account_number: '', ifsc_code: '', branch: '', is_primary: true });
    }
  }, [record, open]);

  const f = (k: 'bank_name' | 'account_number' | 'ifsc_code' | 'branch') =>
    (e: React.ChangeEvent<HTMLInputElement>) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = () => {
    const values = { ...form, employee_id: employeeId };
    if (isEdit) {
      updateRecord(
        { resource: 'employee_bank_details', id: String(record!.id), values },
        { onSuccess: onSaved },
      );
    } else {
      createRecord(
        { resource: 'employee_bank_details', values },
        { onSuccess: onSaved },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Bank Details' : 'Add Bank Account'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <FormField id="bank_name" label="Bank Name" required>
            <Input id="bank_name" value={form.bank_name} onChange={f('bank_name')} placeholder="State Bank of India, HDFC, etc." />
          </FormField>
          <FormField id="account_number" label="Account Number" required>
            <Input id="account_number" value={form.account_number} onChange={f('account_number')} placeholder="Account number" />
          </FormField>
          <FormField id="ifsc_code" label="IFSC Code" required>
            <Input id="ifsc_code" value={form.ifsc_code} onChange={f('ifsc_code')} placeholder="SBIN0001234" className="uppercase" />
          </FormField>
          <FormField id="branch" label="Branch">
            <Input id="branch" value={form.branch} onChange={f('branch')} placeholder="Branch name / location" />
          </FormField>
          <div className="flex items-center gap-2">
            <Checkbox
              id="is_primary"
              checked={form.is_primary}
              onCheckedChange={(checked) => setForm((prev) => ({ ...prev, is_primary: Boolean(checked) }))}
            />
            <Label htmlFor="is_primary" className="cursor-pointer">Set as primary account</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!form.bank_name || !form.account_number || !form.ifsc_code}
          >
            {isEdit ? 'Update' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Emergency Contact Modal ──────────────────────────────────────────────────

function EmergencyModal({
  open,
  record,
  employeeId,
  onClose,
  onSaved,
  createRecord,
  updateRecord,
}: {
  open: boolean;
  record: Record<string, unknown> | null;
  employeeId: string;
  onClose: () => void;
  onSaved: () => void;
  createRecord: ReturnType<typeof useCreate>['mutate'];
  updateRecord: ReturnType<typeof useUpdate>['mutate'];
}) {
  const isEdit = !!record;
  const [form, setForm] = React.useState({
    name: '',
    relationship: '',
    phone: '',
    email: '',
  });

  React.useEffect(() => {
    if (record) {
      setForm({
        name: String(record.name ?? ''),
        relationship: String(record.relationship ?? ''),
        phone: String(record.phone ?? ''),
        email: String(record.email ?? ''),
      });
    } else {
      setForm({ name: '', relationship: '', phone: '', email: '' });
    }
  }, [record, open]);

  const f = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = () => {
    const values = { ...form, employee_id: employeeId };
    if (isEdit) {
      updateRecord(
        { resource: 'employee_emergency_contacts', id: String(record!.id), values },
        { onSuccess: onSaved },
      );
    } else {
      createRecord(
        { resource: 'employee_emergency_contacts', values },
        { onSuccess: onSaved },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Emergency Contact' : 'Add Emergency Contact'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <FormField id="ec_name" label="Name" required>
            <Input id="ec_name" value={form.name} onChange={f('name')} placeholder="Contact full name" />
          </FormField>
          <FormField id="ec_relationship" label="Relationship" required>
            <Input id="ec_relationship" value={form.relationship} onChange={f('relationship')} placeholder="Spouse, Parent, Sibling, etc." />
          </FormField>
          <FormField id="ec_phone" label="Phone" required>
            <Input id="ec_phone" value={form.phone} onChange={f('phone')} placeholder="+91 98765 43210" />
          </FormField>
          <FormField id="ec_email" label="Email">
            <Input id="ec_email" type="email" value={form.email} onChange={f('email')} placeholder="contact@example.com" />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!form.name || !form.relationship || !form.phone}>
            {isEdit ? 'Update' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Document Modal ───────────────────────────────────────────────────────────

const DOCUMENT_TYPES = [
  'aadhaar', 'pan', 'passport', 'driving_license', 'voter_id',
  'offer_letter', 'appointment_letter', 'relieving_letter', 'experience_letter',
  'degree_certificate', 'mark_sheet', 'salary_slip', 'other',
];

function DocumentModal({
  open,
  record,
  employeeId,
  onClose,
  onSaved,
  createRecord,
  updateRecord,
}: {
  open: boolean;
  record: Record<string, unknown> | null;
  employeeId: string;
  onClose: () => void;
  onSaved: () => void;
  createRecord: ReturnType<typeof useCreate>['mutate'];
  updateRecord: ReturnType<typeof useUpdate>['mutate'];
}) {
  const isEdit = !!record;
  const [form, setForm] = React.useState({
    document_type: 'other',
    title: '',
    file_url: '',
    expiry_date: '',
  });

  React.useEffect(() => {
    if (record) {
      setForm({
        document_type: String(record.document_type ?? 'other'),
        title: String(record.title ?? ''),
        file_url: String(record.file_url ?? ''),
        expiry_date: String(record.expiry_date ?? '').slice(0, 10),
      });
    } else {
      setForm({ document_type: 'other', title: '', file_url: '', expiry_date: '' });
    }
  }, [record, open]);

  const f = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = () => {
    const values = { ...form, employee_id: employeeId };
    if (isEdit) {
      updateRecord(
        { resource: 'employee_documents', id: String(record!.id), values },
        { onSuccess: onSaved },
      );
    } else {
      createRecord(
        { resource: 'employee_documents', values },
        { onSuccess: onSaved },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Document' : 'Add Document'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <FormField id="doc_type" label="Document Type" required>
            <select
              id="doc_type"
              value={form.document_type}
              onChange={f('document_type')}
              className="w-full h-9 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 text-sm"
            >
              {DOCUMENT_TYPES.map((t) => (
                <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
              ))}
            </select>
          </FormField>
          <FormField id="doc_title" label="Title" required>
            <Input id="doc_title" value={form.title} onChange={f('title')} placeholder="Document title / description" />
          </FormField>
          <FormField id="doc_file_url" label="File URL">
            <Input id="doc_file_url" value={form.file_url} onChange={f('file_url')} placeholder="https://storage.example.com/doc.pdf" />
          </FormField>
          <FormField id="doc_expiry" label="Expiry Date">
            <Input id="doc_expiry" type="date" value={form.expiry_date} onChange={f('expiry_date')} />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!form.document_type || !form.title}>
            {isEdit ? 'Update' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
